import { useCallback, useEffect, useMemo } from 'react';
import {
  LlmToolRequestActions,
  allSpecPagesBySlug,
  useFetchFsSpec,
  fullstorySelectors,
} from '@fullstory/redux';
import { useSelector } from 'react-redux';
import { PatternRule } from '@fullstory/proto/fs/api/pages';
import {
  useAiLabelSuggestions,
  RequestConfig,
  pathFromPatternRule,
  domainsToString,
} from '@fullstory/library-ui';

interface UseAiPageLabelSuggestionArgs {
  patterns: PatternRule[];
}

export const useAiPageLabelSuggestions = ({ patterns }: UseAiPageLabelSuggestionArgs) => {
  const orgId = useSelector(fullstorySelectors.getOrgId);
  useFetchFsSpec();
  const specPagesBySlug = useSelector(allSpecPagesBySlug);

  const choices = useMemo(
    () => Object.values(specPagesBySlug).map(specPage => specPage.displayName),
    [specPagesBySlug],
  );

  const requestConfigs: Array<{ choices: string[]; useAdditionalContext: boolean }> = [
    {
      choices: [],
      useAdditionalContext: false,
    },
    {
      choices: [],
      useAdditionalContext: false,
    },
    {
      choices,
      useAdditionalContext: true,
    },
    {
      choices: [],
      useAdditionalContext: true,
    },
  ];

  const urlPattern = useMemo(() => {
    const urlPatterns = patterns.map(pattern => {
      const path = pathFromPatternRule(pattern);
      const domain = domainsToString(pattern.domains);
      return `${domain}${path}`;
    });

    return urlPatterns.join(' | ');
  }, [patterns]);

  const buildRequest = useCallback(
    (reqConfig: RequestConfig, blocklist: string[]) =>
      LlmToolRequestActions.suggestPageLabelSingle({
        orgId,
        urlPattern,
        choices: reqConfig.choices,
        blocklist,
        useAdditionalContext: reqConfig.useAdditionalContext,
      }),
    [orgId, urlPattern],
  );

  const getSpecSlug = useCallback(() => null, []);

  const { clearBlockList, ...aiLabelSuggestions } = useAiLabelSuggestions(
    buildRequest,
    getSpecSlug,
    requestConfigs,
  );

  useEffect(() => {
    clearBlockList();
  }, [urlPattern, clearBlockList]);

  return { clearBlockList, ...aiLabelSuggestions };
};
